import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Lock, SlidersHorizontal, Target, X } from "lucide-react";

import { cn } from "../lib/cn.js";
import type { AttributionModelToggle } from "../lib/reports.js";

/**
 * Source Attribution's two mutually-exclusive dropdowns, ported from `ecom_code`'s
 * redesign (owner request, 2026-09-11) onto this app's own design tokens rather than
 * legacy's slate/blue palette — the columns and behaviour are the port, not the CSS.
 *
 * MUTUAL EXCLUSION, not two independent controls. Picking an objective PINS the model
 * set and locks the second dropdown — legacy's `handleSelectObjective`/`handleToggleModel`
 * pair, so a merchant can never end up with an objective selected and a model picker that
 * disagrees with it.
 */
export function AttributionModelPicker({
  toggle,
  selectedObjective,
  selectedModelKeys,
  onSelectObjective,
  onToggleModel,
  onResetModels,
}: {
  readonly toggle: AttributionModelToggle;
  readonly selectedObjective: string | null;
  readonly selectedModelKeys: ReadonlySet<string>;
  readonly onSelectObjective: (key: string | null) => void;
  readonly onToggleModel: (key: string) => void;
  readonly onResetModels: () => void;
}): React.JSX.Element {
  const [objectiveOpen, setObjectiveOpen] = useState(false);
  const [modelsOpen, setModelsOpen] = useState(false);
  const objectiveRef = useRef<HTMLDivElement>(null);
  const modelsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(event: MouseEvent): void {
      if (objectiveRef.current && !objectiveRef.current.contains(event.target as Node)) {
        setObjectiveOpen(false);
      }
      if (modelsRef.current && !modelsRef.current.contains(event.target as Node)) {
        setModelsOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const activeObjective = toggle.objectives.find((objective) => objective.key === selectedObjective) ?? null;
  const locked = activeObjective !== null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative" ref={objectiveRef}>
        <button
          type="button"
          onClick={() => {
            setObjectiveOpen((open) => !open);
            setModelsOpen(false);
          }}
          className={cn(
            "flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium shadow-xs transition-colors",
            locked
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
          )}
        >
          <Target className={cn("size-3.5 shrink-0", locked ? "text-primary" : "text-muted-foreground")} />
          <span>{activeObjective !== null ? `Objective: ${activeObjective.title}` : "What is your objective?"}</span>
          {activeObjective !== null ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                onSelectObjective(null);
              }}
              className="hover:bg-primary/20 -mr-1 flex items-center justify-center rounded-full p-0.5"
              title="Clear objective"
            >
              <X className="size-3.5" />
            </span>
          ) : (
            <ChevronDown className={cn("text-muted-foreground size-3.5 transition-transform", objectiveOpen && "rotate-180")} />
          )}
        </button>

        {objectiveOpen && (
          <div className="bg-popover text-popover-foreground absolute right-0 z-40 mt-1.5 w-80 space-y-1.5 rounded-xl border p-2 shadow-lg sm:w-96">
            <div className="border-b px-2 py-1.5">
              <h4 className="text-xs font-semibold">Choose an optimisation objective</h4>
              <p className="text-muted-foreground mt-0.5 text-[11px]">
                Picking one pins the model below and turns off the multi-select.
              </p>
            </div>
            <div className="space-y-1 pt-1">
              {toggle.objectives.map((objective) => {
                const isSelected = selectedObjective === objective.key;
                return (
                  <div
                    key={objective.key}
                    onClick={() => {
                      onSelectObjective(isSelected ? null : objective.key);
                      setObjectiveOpen(false);
                    }}
                    className={cn(
                      "cursor-pointer rounded-lg border p-2.5 transition-colors",
                      isSelected ? "border-primary/40 bg-primary/10" : "border-transparent hover:bg-accent",
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold">{objective.title}</p>
                        <p className="text-muted-foreground mt-0.5 text-[11px] leading-relaxed">
                          {objective.description}
                        </p>
                      </div>
                      {isSelected && (
                        <span className="bg-primary text-primary-foreground mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full">
                          <Check className="size-3" strokeWidth={3} />
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="relative" ref={modelsRef}>
        <button
          type="button"
          disabled={locked}
          title={locked ? "Attribution model picker is disabled while an objective is active." : undefined}
          onClick={() => {
            if (locked) return;
            setModelsOpen((open) => !open);
            setObjectiveOpen(false);
          }}
          className={cn(
            "flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium shadow-xs transition-colors",
            locked
              ? "border-input bg-muted text-muted-foreground cursor-not-allowed opacity-75"
              : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
          )}
        >
          {locked ? (
            <Lock className="text-muted-foreground size-3.5 shrink-0" />
          ) : (
            <SlidersHorizontal className="text-muted-foreground size-3.5 shrink-0" />
          )}
          <span>Attribution models ({selectedModelKeys.size})</span>
          <ChevronDown className={cn("text-muted-foreground size-3.5 transition-transform", modelsOpen && "rotate-180")} />
        </button>

        {modelsOpen && !locked && (
          <div className="bg-popover text-popover-foreground absolute right-0 z-40 mt-1.5 w-72 space-y-1.5 rounded-xl border p-2 shadow-lg sm:w-80">
            <div className="flex items-center justify-between border-b px-2 py-1.5">
              <div>
                <h4 className="text-xs font-semibold">Select attribution models</h4>
                <p className="text-muted-foreground text-[11px]">Shown side by side with Platform and EA Matrix.</p>
              </div>
              <button
                type="button"
                onClick={onResetModels}
                className="text-muted-foreground hover:text-foreground text-[11px] font-medium"
              >
                Reset
              </button>
            </div>
            <div className="space-y-1 pt-1">
              {toggle.models.map((model) => {
                const isChecked = selectedModelKeys.has(model.key);
                return (
                  <div
                    key={model.key}
                    onClick={() => onToggleModel(model.key)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2.5 rounded-lg border p-2 transition-colors",
                      isChecked ? "border-primary/30 bg-primary/5" : "border-transparent hover:bg-accent",
                    )}
                  >
                    <span
                      className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded border",
                        isChecked ? "bg-primary border-primary text-primary-foreground" : "border-input",
                      )}
                    >
                      {isChecked && <Check className="size-3" strokeWidth={3} />}
                    </span>
                    <span className="text-xs font-medium">{model.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
